'use memo';

import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { AlertCard } from '@/components/AlertCard';
import { Badge } from '@/components/Badge';
import IconButton from '@/components/Button/IconButton';
import { Card } from '@/components/Card/card';
import { ConfirmActionDialog } from '@/components/Dialog/ConfirmActionDialog';
import { HStack, VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { Text } from '@/components/Typography/Text';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { EvaluationOrderNote, PolicyStatusBadge, authPoliciesKeyPrefix, swapAt, useAuthenticationPolicyList, useDeleteAuthenticationPolicy, useReorderAuthenticationPolicies } from '@/features/authentication-policy';
import { PolicySectionHeader } from '@/features/identity-policy';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { handleApiError } from '@/lib/api/error';
import type { AuthenticationPolicySummary } from '@/lib/api/orpc/contracts/authentication-policy';
import { defaultNS, useTranslation } from '@/lib/i18n';
export const Route = createFileRoute('/_staff/settings/authentication/_layout/policies/')({
  component: AuthPoliciesPage
});

/** 表示順は優先度昇順。デフォルトポリシーは priority に関係なく常に最下段。 */
function sortForDisplay(policies: AuthenticationPolicySummary[]): AuthenticationPolicySummary[] {
  return [...policies].sort((a, b) => {
    if (a.isDefault !== b.isDefault) return a.isDefault ? 1 : -1;
    return a.priority - b.priority;
  });
}

/** デフォルトポリシーは常に最下位なので動かせない。 */
function swap(policies: AuthenticationPolicySummary[], index: number, offset: -1 | 1): AuthenticationPolicySummary[] {
  return swapAt(policies, index, offset, policy => !policy.isDefault);
}

/** draft を作った時点と id 集合が一致しているか（別の管理者の追加・削除を検知する）。 */
function matchesServerPolicies(draft: AuthenticationPolicySummary[], items: AuthenticationPolicySummary[]): boolean {
  if (draft.length !== items.length) return false;
  const ids = new Set(items.map(policy => policy.id));
  return draft.every(policy => ids.has(policy.id));
}
function AuthPoliciesPage() {
  const {
    i18n,
    t
  } = useTranslation(defaultNS, {
    keyPrefix: authPoliciesKeyPrefix
  });
  const {
    t: tReorder
  } = useTranslation(defaultNS, {
    keyPrefix: 'component.reorderableList'
  });
  const tc = useCommonTranslation();
  const {
    data,
    isPending,
    isError
  } = useAuthenticationPolicyList();
  const reorderMutation = useReorderAuthenticationPolicies();
  const deleteMutation = useDeleteAuthenticationPolicy();

  // 並び替えは編集モードに入ってからローカル draft を作り、保存時に一括更新する
  const [draft, setDraft] = useState<AuthenticationPolicySummary[] | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AuthenticationPolicySummary | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  if (isPending) return <ViewportLoader />;
  if (isError || data == null) {
    return <VStack className="w-full gap-4 px-12 py-6">
        <PolicySectionHeader iconName="ShieldCheckIcon" title={t($ => $.title)} description={t($ => $.description)} />
        <AlertCard variant="mutedError" description={t($ => $.list.loadError)} />
      </VStack>;
  }

  // 別の管理者がポリシーを追加・削除したら draft を捨てる（消えた id を送らないため）
  const validDraft = draft != null && matchesServerPolicies(draft, data.items) ? draft : null;
  const displayed = validDraft ?? sortForDisplay(data.items);
  const isReordering = validDraft != null;
  const reorderableCount = displayed.filter(policy => !policy.isDefault).length;
  const handleSaveOrder = async () => {
    if (validDraft == null) return;
    setErrorMessage('');
    try {
      await reorderMutation.mutateAsync({
        // デフォルトポリシーは常に最下位なので送らない。先頭 = 最優先。
        orderedIds: validDraft.filter(policy => !policy.isDefault).map(policy => policy.id)
      });
      setDraft(null);
      toast({
        description: t($ => $.toast.reordered)
      });
    } catch (error) {
      setErrorMessage(handleApiError(error, {
        language: i18n.language
      }).message);
    }
  };
  const handleDelete = async () => {
    if (deleteTarget == null) return;
    try {
      await deleteMutation.mutateAsync({
        id: deleteTarget.id
      });
      setDeleteTarget(null);
      toast({
        description: t($ => $.toast.deleted)
      });
    } catch (error) {
      toast({
        description: handleApiError(error, {
          language: i18n.language
        }).message,
        variant: 'destructive'
      });
    }
  };
  return <VStack className="w-full gap-4 px-12 py-6">
      <PolicySectionHeader iconName="ShieldCheckIcon" title={t($ => $.title)} description={t($ => $.description)} />

      <EvaluationOrderNote />

      <HStack className="items-center justify-between gap-2">
        <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
          {t($ => $.list.priorityNote)}
        </Text>
        <HStack className="shrink-0 items-center gap-2">
          {isReordering ? <>
              <Button variant="ghost" disabled={reorderMutation.isPending} onClick={() => {
            setDraft(null);
            setErrorMessage('');
          }}>
                {t($ => $.list.reorder.cancel)}
              </Button>
              <Button disabled={reorderMutation.isPending} onClick={() => void handleSaveOrder()}>
                {t($ => $.list.reorder.save)}
              </Button>
            </> : <>
              <Button variant="outline" disabled={reorderableCount < 2} onClick={() => setDraft(sortForDisplay(data.items))}>
                {t($ => $.list.reorder.edit)}
              </Button>
              <Button asChild>
                <Link to="/settings/authentication/policies/new">
                  {t($ => $.list.create)}
                </Link>
              </Button>
            </>}
        </HStack>
      </HStack>

      {errorMessage !== '' && <Text variant="labelSmallReg" className="text-error-on-neutral-fill">
          {errorMessage}
        </Text>}

      {displayed.length === 0 ? <Card className="p-6">
          <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
            {t($ => $.list.empty)}
          </Text>
        </Card> : <VStack className="gap-2">
          {displayed.map((policy, index) => <Card key={policy.id} className="p-4">
              <HStack className="items-center gap-3">
                {isReordering && <HStack className="shrink-0 items-center gap-0.5">
                    <IconButton icon="ChevronUpIcon" size="Small" variant="ghost"
            // デフォルトは動かせず、その 1 つ手前も下へは動かせない
            disabled={policy.isDefault || index === 0} aria-label={tReorder('moveUp')} onClick={() => setDraft(swap(displayed, index, -1))} />
                    <IconButton icon="ChevronDownIcon" size="Small" variant="ghost" disabled={policy.isDefault || index >= reorderableCount - 1} aria-label={tReorder('moveDown')} onClick={() => setDraft(swap(displayed, index, 1))} />
                  </HStack>}

                <Text variant="labelSmall" className="w-6 shrink-0 text-center text-neutral-muted-on-fill">
                  {index + 1}
                </Text>

                <VStack className="min-w-0 flex-1 gap-1">
                  <HStack className="items-center gap-2">
                    {isReordering ? <Text variant="labelSemiLarge" truncate>
                        {policy.name}
                      </Text> : <Link to="/settings/authentication/policies/$id" params={{
                id: policy.id
              }} className="min-w-0">
                        <Text variant="labelSemiLarge" truncate className="text-primary-on-neutral-fill">
                          {policy.name}
                        </Text>
                      </Link>}
                    {policy.isDefault && <Badge variant="primaryMuted" className="shrink-0">
                        {t($ => $.list.defaultBadge)}
                      </Badge>}
                    <PolicyStatusBadge status={policy.status} />
                  </HStack>
                  {policy.description != null && policy.description !== '' && <Text variant="labelSmallReg" truncate className="text-neutral-muted-on-fill">
                      {policy.description}
                    </Text>}
                </VStack>

                <Text variant="labelSmallReg" className="shrink-0 text-nowrap text-neutral-muted-on-fill">
                  {t($ => $.list.ruleCountValue, {
              count: policy.ruleCount
            })}
                </Text>
                <Text variant="labelSmallReg" className="shrink-0 text-nowrap text-neutral-muted-on-fill">
                  {t($ => $.list.attachmentCountValue, {
              count: policy.attachmentCount
            })}
                </Text>

                {!isReordering && !policy.isDefault && <IconButton icon="Trash2Icon" size="Small" variant="ghost" aria-label={tc('action.delete')} onClick={() => setDeleteTarget(policy)} />}
              </HStack>
            </Card>)}
        </VStack>}

      <ConfirmActionDialog isOpen={deleteTarget != null} isLoading={deleteMutation.isPending} title={t($ => $.delete.title)} description={(deleteTarget?.attachmentCount ?? 0) > 0 ? t($ => $.delete.descriptionReferenced, {
      count: deleteTarget?.attachmentCount ?? 0
    }) : t($ => $.delete.descriptionUnreferenced)} okLabel={t($ => $.delete.confirm)} okVariant="danger" onClose={() => setDeleteTarget(null)} onConfirm={() => {
      void handleDelete();
    }} />
    </VStack>;
}